import React, { useMemo } from 'react';
import { FunnelPlotOutlined } from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const FUNNEL_STAGES = [
  {
    key: 'sent',
    label: 'Sent',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    shadow: 'rgba(59,130,246,0.3)',
    textColor: '#93c5fd',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    shadow: 'rgba(6,182,212,0.3)',
    textColor: '#67e8f9',
  },
  {
    key: 'opened',
    label: 'Opened',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    shadow: 'rgba(16,185,129,0.3)',
    textColor: '#6ee7b7',
  },
  {
    key: 'clicked',
    label: 'Clicked',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    shadow: 'rgba(139,92,246,0.3)',
    textColor: '#c4b5fd',
  },
  {
    key: 'replied',
    label: 'Replied',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
    shadow: 'rgba(168,85,247,0.3)',
    textColor: '#d8b4fe',
  },
];

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const FunnelChart = ({ funnel = {} }) => {
  const stageData = useMemo(() => {
    const sent = funnel.sent || 0;
    return FUNNEL_STAGES.map((stage) => {
      const count = funnel[stage.key] || 0;
      const percentage = sent > 0 ? ((count / sent) * 100).toFixed(1) : '0.0';
      const widthPct = sent > 0 ? Math.max((count / sent) * 100, 8) : 8;
      return {
        ...stage,
        count,
        percentage,
        widthPct,
      };
    });
  }, [funnel]);

  const hasData = (funnel.sent || 0) > 0;

  if (!hasData) {
    return (
      <div style={{ ...glassCard, padding: 32, textAlign: 'center' }}>
        <FunnelPlotOutlined style={{ fontSize: 28, color: COLORS.textMuted, marginBottom: 8 }} />
        <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>
          No funnel data available yet
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...glassCard, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FunnelPlotOutlined style={{ color: '#3b82f6', fontSize: 16 }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Engagement Funnel</span>
      </div>

      {/* Funnel Bars */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'center',
        padding: '8px 0',
      }}>
        {stageData.map((stage, idx) => {
          const conversionFromPrev = idx > 0 && stageData[idx - 1].count > 0
            ? ((stage.count / stageData[idx - 1].count) * 100).toFixed(1)
            : null;

          return (
            <div
              key={stage.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: 2,
              }}
            >
              {/* Conversion arrow between stages */}
              {idx > 0 && conversionFromPrev !== null && (
                <div style={{
                  fontSize: 10,
                  color: COLORS.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 0',
                }}>
                  <span style={{
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: `5px solid ${COLORS.textMuted}`,
                  }} />
                  {conversionFromPrev}%
                </div>
              )}

              {/* Bar */}
              <div
                style={{
                  width: `${stage.widthPct}%`,
                  minWidth: 120,
                  maxWidth: '100%',
                  background: stage.gradient,
                  borderRadius: 10,
                  padding: '12px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: `0 4px 16px ${stage.shadow}`,
                  transition: 'width 0.6s ease, transform 0.2s ease',
                  cursor: 'default',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {/* Glass shimmer overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 100%)',
                  borderRadius: '10px 10px 0 0',
                  pointerEvents: 'none',
                }} />

                <span style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#fff',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  {stage.label}
                </span>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  position: 'relative',
                  zIndex: 1,
                }}>
                  <span style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: '#fff',
                  }}>
                    {stage.count.toLocaleString()}
                  </span>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: stage.textColor,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.15)',
                  }}>
                    {stage.percentage}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FunnelChart;
